// runtime.ts — the ONE client runtime store (MVU root) and the one effect
// runner. See .claude/analysis/client-state-machine-2026-09-30/chat-module-plan.md
// §1.3. Not wired into the app yet (step S6).
//
// The root Model is the product of the mounted modules' Models: each `mount`
// adds one cell and returns that module's typed handle (`Mounted`). Mounting
// rather than passing a keyed record keeps every module's Model/Msg/Effect
// types intact without a cast (a keyed root would need a correlated-union
// cast to build and index).
//
// `dispatch(msg)` on a handle:
//   1. runs the module's pure `update`; a refusal leaves the model untouched
//      and is returned to the caller;
//   2. commits the next model and notifies subscribers;
//   3. hands each Effect, in order, to the module's interpreter (`run`), whose
//      outcome Msgs re-enter through the same `dispatch`;
//   4. offers each Signal, in order, to every mounted module's `inbound`
//      (mount order) and dispatches the Msgs they produce. A listener refusing
//      a routed Msg is the listener's business (e.g. a stop request while
//      idle); the refusal is not propagated to the emitter. Then hands it to
//      every `onSignal` listener: owners that are not modules yet (the shell
//      still owns frame / toasts / the world re-read until S6).
//
// Impure by nature (it owns the mutable cells and calls the interpreters);
// everything it calls except `run` is pure. `subscribe` + `getModel` are
// shaped for React's `useSyncExternalStore`.

import { Result } from "@bilda/utils";
import type { Module, Step } from "../modules/module.js";
import type { Signal } from "../modules/signal.js";

type Mounted<Model, Msg, Effect, Refusal> = {
    readonly dispatch: (
        msg: Msg,
    ) => Result.Result<Step<Model, Effect>, Refusal>;
    readonly getModel: () => Model;
};

export type Runtime = {
    readonly mount: <Model, Msg, Effect, Refusal>(
        module: Module<Model, Msg, Effect, Refusal>,
    ) => Mounted<Model, Msg, Effect, Refusal>;
    // Fires after any module commits a new model.
    readonly subscribe: (listener: () => void) => () => void;
    // Every routed Signal, after the modules were offered it.
    readonly onSignal: (listener: (signal: Signal) => void) => () => void;
    // Routes a Signal raised outside the modules (a shell door, e.g. the
    // keyboard's stop request), exactly as a module-emitted one.
    readonly emit: (signal: Signal) => void;
};

export const mkRuntime = (): Runtime => {
    const listeners = new Set<() => void>();
    const signalListeners = new Set<(signal: Signal) => void>();
    const inboxes: Array<(signal: Signal) => void> = [];

    const route = (signal: Signal): void => {
        for (const offer of [...inboxes]) offer(signal);
        for (const listener of [...signalListeners]) listener(signal);
    };

    const mount = <Model, Msg, Effect, Refusal>(
        module: Module<Model, Msg, Effect, Refusal>,
    ): Mounted<Model, Msg, Effect, Refusal> => {
        let model = module.init;

        const dispatch = (
            msg: Msg,
        ): Result.Result<Step<Model, Effect>, Refusal> => {
            const result = module.update(model, msg);
            if (Result.isErr(result)) return result;
            const step = result.value;
            model = step.model;
            for (const listener of [...listeners]) listener();
            for (const effect of step.effects) module.run(effect, dispatch);
            for (const signal of step.signals) route(signal);
            return result;
        };

        inboxes.push((signal) => {
            const msg = module.inbound(signal);
            if (msg !== undefined) dispatch(msg);
        });

        return { dispatch, getModel: () => model };
    };

    return {
        mount,
        emit: route,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        onSignal: (listener) => {
            signalListeners.add(listener);
            return () => {
                signalListeners.delete(listener);
            };
        },
    };
};
